// LA COMISIÓN DE UNA PÓLIZA: UNA SOLA CUENTA PARA TODAS LAS PANTALLAS.
//
// Lissette, 2026-10-11, mirando el Reporte de Ventas: *"¿de dónde sacas esas
// comisiones? … si aquí se llena y no está allá en comisiones por póliza, que
// se llene, y viceversa: estos datos tienen que ser los mismos en los dos
// lados"*.
//
// Los dos lados ya escribían en los MISMOS campos de la póliza, pero cada uno
// con su propia idea: Comisiones por Póliza calculaba el total (salud / vida /
// % de la prima) y el Reporte de Ventas dejaba escribir el total a mano encima,
// que la tabla volvía a pisar al siguiente guardado. Aquí vive la cuenta y el
// guardado; las dos pantallas los importan y ya no pueden decir cosas
// distintas.
//
// La aritmética de cada formato está en comisionPorTipo.js (vida, % de la
// prima) y comisionPorPersona.js (salud). Esto solo decide cuál toca.
import { formatoDeComision, comisionDeVida, comisionPorPrima } from './comisionPorTipo.js';
import { personasAseguradas, comisionPorMiembro, cobraPorMiembro, numero } from './comisionPorPersona.js';
import { mesesQueDioDinero } from './mesesDePoliza.js';
import { primaElegida } from './primaDePoliza.js';

/** Lo que se edita: [clave corta, campo de la póliza]. */
export const CAMPOS_DE_COMISION = [
  ['pct', 'commissionPercent'], // % split (lo que nos queda)
  ['monthly', 'commissionMonthly'], // forma vieja, ya no se enseña; sigue contando
  ['perPerson', 'comisionPorPersona'],
  ['razon', 'razonDeComision'],
  ['pctPrima', 'porcentajeDePrima'],
  ['adelanto', 'mesesDeAdelanto'],
  ['amt', 'commissionAmount'], // el total; escrito a mano solo si no hay cuenta
];

/** Los valores de la póliza con el borrador encima (lo que se está escribiendo). */
export function valoresDeComision(poliza, borrador = {}) {
  const v = {};
  for (const [k, campo] of CAMPOS_DE_COMISION) {
    v[k] = borrador[k] !== undefined ? borrador[k] : (poliza?.[campo] ?? '');
  }
  return v;
}

/**
 * El total de la póliza y cómo salió.
 *
 *   como = 'vida' | 'prima' | 'miembro' | 'mensual' | 'manual'
 *
 * Mientras una fila no tenga los datos de su forma nueva, se cuenta COMO ANTES
 * (por miembro, mensual guardado × meses, o el total escrito a mano): así
 * ninguna póliza ya cargada cambia de total sola.
 */
export function cuentaDeComision(poliza, valores = valoresDeComision(poliza)) {
  const v = valores;
  const formato = formatoDeComision(poliza?.type);
  if (formato === 'vida') {
    const vida = comisionDeVida({ primaMensual: poliza?.monthlyPremium, porcentajeDePrima: v.pctPrima, split: v.pct, meses: v.adelanto });
    if (vida) return { total: vida.total, como: 'vida', vida, formato };
  }
  if (formato === 'prima') {
    // La prima que esté llena, o la elegida si hay varias (Lissette
    // 2026-10-22). En Medicare y los tipos sin las tres, la mensual de siempre.
    const total = comisionPorPrima({ prima: primaElegida(poliza).monto, porcentajeDePrima: v.pctPrima, split: v.pct });
    if (total != null) return { total, como: 'prima', formato };
  }
  const m = { personas: personasAseguradas(poliza), porPersona: v.perPerson, porcentaje: v.pct };
  if (cobraPorMiembro(m)) return { total: comisionPorMiembro(m), como: 'miembro', formato };
  const mensual = numero(v.monthly);
  if (mensual > 0) return { total: mensual * mesesQueDioDinero(poliza).meses, como: 'mensual', formato };
  return { total: numero(v.amt), como: 'manual', formato };
}

/**
 * La póliza lista para guardar con lo que se escribió, y si cambió algo.
 *
 * El total (`commissionAmount`) se guarda DERIVADO con la misma cuenta que se
 * enseña, para que la tarjeta de la póliza, los reportes y las dos tablas lean
 * la misma cifra. Solo si no hay cuenta posible se guarda lo escrito a mano.
 */
export function polizaConComision(poliza, borrador = {}) {
  const v = valoresDeComision(poliza, borrador);
  const c = cuentaDeComision(poliza, v);
  const amt = c.como === 'manual' ? String(v.amt ?? '') : String(c.total);
  const siguiente = { ...poliza };
  for (const [k, campo] of CAMPOS_DE_COMISION) siguiente[campo] = k === 'amt' ? amt : v[k];
  const cambio = CAMPOS_DE_COMISION.some(([, campo]) => String(siguiente[campo] ?? '') !== String(poliza?.[campo] ?? ''));
  return { siguiente, cambio, cuenta: c };
}
